<template>
    <v-card :loading="loading">
        <v-card-title class="text-h5">
            <v-btn
                class="mr-3"
                depressed
                color="primary"
                icon
                rounded
                @click="closeDialog"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            Shipping Form
        </v-card-title>
        <v-card-text>
            <v-form ref="form" v-model="valid">
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            v-model="shipping.country"
                            :items="countries"
                            prepend-icon="mdi-earth"
                            label="Country"
                            required
                            @change="selectState"
                            :rules="[rules.required]"
                            :disabled="!createForm"
                        />
                    </v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            v-model="shipping.state"
                            @change="selectCity"
                            :items="states"
                            prepend-icon="mdi-map-marker-radius"
                            label="State *"
                            required
                            :rules="[rules.required]"
                            :disabled="!createForm"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-combobox
                            v-model="shipping.city"
                            :filter="filter"
                            :hide-no-data="!search"
                            :items="cities"
                            item-value="text"
                            item-text="text"
                            :return-object="false"
                            hide-details
                            prepend-icon="mdi-city"
                            :search-input.sync="search"
                            hide-selected
                            :disabled="!createForm"
                            label="City *"
                            flat
                            @focus="enabled = false"
                            @blur="enabled = true"
                        >
                            <template v-slot:item="{ index, item }">
                                <v-text-field
                                    v-if="editing === item"
                                    v-model="editing.text"
                                    autofocus
                                    flat
                                    background-color="transparent"
                                    hide-details
                                    solo
                                    @keyup.enter="edit(index, item)"
                                    required
                                    :rules="[rules.required]"
                                ></v-text-field>
                                <span v-else>
                                    {{ item.text }}
                                </span>
                                <v-spacer></v-spacer>
                            </template>
                        </v-combobox>
                    </v-col>
                    <v-col cols="6">
                        <v-text-field
                            type="number"
                            v-model="shipping.shippingCost"
                            underlined
                            prepend-icon="mdi-text"
                            label="Shipping Cost *"
                            :rules="[rules.required]"
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="6">
                        <v-text-field
                            type="number"
                            v-model="shipping.internalShipping"
                            underlined
                            prepend-icon="mdi-text"
                            label="Internal Shipping"
                        />
                    </v-col>
                    <v-col cols="4">
                        <v-select
                            v-model="shipping.currency"
                            underlined
                            prepend-icon="mdi-currency-usd"
                            label="Currency *"
                            :rules="[rules.required]"
                            :items="currencies"
                        />
                    </v-col>
                </v-row>
            </v-form>

            <small v-if="createForm">* indicates required field</small>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
                text
                color="primary"
                :loading="loading"
                :disabled="!valid || !enabled"
                @click="saveShipping"
            >
                Save
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'
const countrycitystate = require('countrycitystatejson')
import { objDiff } from '@/helpers/objDiff'
import API from '@/services/api'
import { storage } from '@/services/firebase'

export default {
    name: 'ShippingForm',
    props: {
        originalShipping: {
            type: Object,
        },
        createForm: {
            type: Boolean,
            required: true,
        },
        setting: {
            type: Object,
            required: true,
        },
        items: {
            type: Array,
            required: true,
        },
        editSettingPermission: { type: Boolean, required: true },
    },
    data() {
        return {
            loading: false,
            valid: false,
            rules: {
                required: v => !!v || 'Required',
            },
            listCountries: null,
            countries: [],
            cities: [],
            states: [],
            shortNameCity: null,
            editing: null,
            search: null,
            enabled: true,
            shipping: {},
            currencies: ['USD', 'COP'],
            companyId: JSON.parse(localStorage.getItem('company')),
        }
    },
    computed: {
        shippingDiff: function() {
            if (!this.createForm) {
                return objDiff(this.originalShipping, this.shipping)
            } else {
                return null
            }
        },
    },
    async mounted() {
        try {
            if (!this.createForm) {
                this.shipping = _.cloneDeep(this.originalShipping)
                this.selectState(this.shipping.country)
                this.selectCity(this.shipping.state)
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        closeDialog: function() {
            this.$refs.form.reset()
            this.$emit('close')
        },
        difference(array1, array2) {
            const set2 = new Set(array2)
            return array1.filter(element => !set2.has(element))
        },
        saveShipping() {
            if (this.createForm) {
                this.createShipping()
            } else {
                this.updateShipping()
            }
        },
        async createShipping() {
            try {
                this.loading = true
                // Assign only the name of the city
                if (this.shipping.city && this.shipping.city.text) {
                    this.shipping.city = this.shipping.city.text
                }
                // clean data
                Object.keys(this.shipping).forEach(key => {
                    if (
                        this.shipping[key] === undefined ||
                        this.shipping[key] === null
                    ) {
                        delete this.shipping[key]
                    }
                })
                const shipping = await API.createShipping({
                    settingId: this.setting.id,
                    ...this.shipping,
                })
                this.$emit('addShipping', shipping)
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async updateShipping() {
            try {
                this.loading = true
                const index = this.items.findIndex(
                    i => i.id === this.shipping.id
                )
                if (index > -1 && this.editSettingPermission) {
                    this.items.splice(index, 1, {
                        ...this.items[index],
                        ...this.shippingDiff,
                    })
                    const blob = new Blob([JSON.stringify(this.items)], {
                        type: 'application/json',
                    })
                    const path = `${this.companyId}/shipping/shippingData.json`
                    const storageRef = storage().ref(path)
                    await storageRef.put(blob)
                    this.$emit('replaceShipping', this.items)
                }
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        selectState: function(event) {
            const index = this.listCountries.findIndex(x => x.name === event)
            this.shortNameCity = this.listCountries[index].shortName
            this.states = countrycitystate.getStatesByShort(this.shortNameCity)
        },
        selectCity: function(event) {
            this.cities = []
            this.cities[0] = { header: 'Select or create one' }
            let data = countrycitystate.getCities(this.shortNameCity, event)
            if (data.length == 0) {
                this.cities = []
            }
            data.forEach(city => {
                this.cities.push({ text: city })
            })
        },
        filter(item, queryText, itemText) {
            if (item.header) return false
            const hasValue = val => (val != null ? val : '')
            const text = hasValue(itemText)
            const query = hasValue(queryText)

            return (
                text
                    .toString()
                    .toLowerCase()
                    .indexOf(query.toString().toLowerCase()) > -1
            )
        },
    },
    async created() {
        this.listCountries = countrycitystate.getCountries()
        this.countries = this.listCountries.map(x => x.name)
    },
}
</script>
